import { savedSellerLabel, showsSavedOwnerInsteadOfAd } from "hooks/contacts/savedListingSeller";
import type { SavedListingSeller } from "hooks/contacts/savedListingSeller";
import type { CalendarEventType, ListingContact } from "../../../@types/sharedSchema";

/**
 * THE BOARD'S SELLER SIDE ONCE SHE HAS SAVED THE OWNER — the rules the table row and the grid
 * card share (Trello #254, follow-up of 2026-11-04).
 *
 * Saving an ad's owner from the board files him under the number she typed — usually his real
 * number, typed over the ad's Yad2 mask. The save button learned to recognise him (the listing
 * link), but the row kept printing the AD: its seller name, the mask as the number to call and
 * copy, a ⚠ beside it, and a task or meeting made from the row invited the mask. The support
 * rep's report, word for word: the bug is fixed, but the phone "still stays virtual", and tasks
 * and meetings "do not carry the owner's original number". The customer's own words: the seller
 * and his property should be ONE thing, as in her previous CRM.
 *
 * So once a contact of hers stands for an ad's owner THROUGH THE LISTING LINK, the row is his:
 * his saved name, his number (shown, dialled, copied, invited), no mask warning.
 *
 * Only for a MARKET ad. On her own (`webot`) listing the row prints the owner recorded on the
 * listing document, which is hers to edit in the listing form; a contact linked to it from a
 * contact card does not override what the listing itself says. And only `via: 'listing'`: when
 * the ad's own number is already a contact of hers, the ad shows the right number and nothing
 * needs replacing.
 */
export type BoardSavedOwner = {
  contactId: string;
  /** The saved name as stored — what an invited attendee is called. */
  name?: string;
  /** What the row prints: the saved name, with "+N" when more contacts are linked. */
  label?: string;
  /** His own number (E.164): shown, dialled, copied and invited. */
  phone: string;
};

export function savedOwnerOverAd(
  saved: SavedListingSeller | null | undefined,
  isListingFromWebot: boolean
): BoardSavedOwner | null {
  if (isListingFromWebot || !showsSavedOwnerInsteadOfAd(saved)) return null;
  return {
    contactId: saved.contactId,
    name: saved.name,
    label: saved.name ? savedSellerLabel(saved) : undefined,
    phone: saved.phone
  };
}

/** The attendee shape `parseContactsToAttendees` takes. */
export type BoardEventAttendee = {
  firstName: string;
  lastName: string;
  phoneNumberInternational: string;
};

const splitName = (name?: string): { firstName: string; lastName: string } => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
};

/**
 * WHO a task or meeting made from a board row is with: the owner she saved (his real number —
 * a meeting's attendee is sent the invitation on WhatsApp, and the ad's Yad2 mask reaches nobody),
 * else the person the ad names, else nobody. Never the listing agent: on a webot listing with no
 * recorded owner the only person the row knows is the realtor herself.
 */
export function boardEventAttendee({
  savedOwner,
  seller,
  sellerIsSaveable
}: {
  savedOwner: BoardSavedOwner | null;
  seller: { name?: string; phone?: string };
  sellerIsSaveable: boolean;
}): BoardEventAttendee | null {
  if (savedOwner) return { ...splitName(savedOwner.name), phoneNumberInternational: savedOwner.phone };
  if (!seller.phone || !sellerIsSaveable) return null;
  return { ...splitName(seller.name), phoneNumberInternational: seller.phone };
}

/**
 * THE SELLER THE "ADD TO WEBOT" FORM OPENS WITH — the owner she saved, not the ad.
 *
 * Adding a market ad to her listings ("הוספה ל-Webot", which opens the listing form as a copy)
 * seeds the form from the AD (`convertProcessedListingDataToListing`): its seller name and its
 * phone, usually a Yad2 mask. Once she has saved the owner from the board, the row beside the
 * menu already prints him — and the form still opened on the ad's name and the mask, with the
 * mask warning under it. Reported by support the day the row learned to print him (2026-11-04):
 * "it duplicates the listing, and the owner part keeps the virtual number, not what I fixed".
 *
 * So when a saved owner stands in for the ad (`savedOwnerOverAd`), the form's seller IS him:
 * his number, and his `contactId`, so the save links the listing to that contact rather than
 * resolving a number again (`persistListingSeller` adds the link and never rewrites his card).
 * His saved name; a contact saved without a name keeps the ad's name, as the row shows it.
 * Without a saved owner the seed is returned as it is.
 */
export function withSavedOwnerAsSeller<T extends { listingContacts?: ListingContact[] | null }>(
  seed: T,
  savedOwner: BoardSavedOwner | null
): T {
  if (!savedOwner) return seed;
  const adSeller = seed.listingContacts?.[0];
  const { firstName, lastName } = savedOwner.name
    ? splitName(savedOwner.name)
    : { firstName: adSeller?.callerFirstName ?? "", lastName: adSeller?.callerLastName ?? "" };
  return {
    ...seed,
    listingContacts: [
      {
        callerFirstName: firstName,
        callerLastName: lastName,
        callerPhoneNumber: savedOwner.phone,
        contactId: savedOwner.contactId
      }
    ]
  };
}

type BoardAddress =
  | { street?: string | null; houseNumber?: string | number | null; city?: string | null }
  | null
  | undefined;

/**
 * The default title of a task / meeting made from a board row: "משימה – נשר 37". The house
 * number is in it because one street often carries several of her ads (three on סחלב 1 in one
 * customer's book), and a reminder that says only the street does not say which.
 */
export function boardEventTitle(type: CalendarEventType, address: BoardAddress): string {
  const kind = type === "task" ? "משימה" : "פגישה";
  const street = [address?.street, address?.houseNumber]
    .map((part) => (part === null || part === undefined ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" ");
  const place = street || address?.city?.trim() || "";
  return place ? `${kind} – ${place}` : kind;
}
